import {
  HELLO_ERR,
  HELLO_REQ,
  HELLO_RES,
  LOGOUT,
  POST_LOGIN_ERR,
  POST_LOGIN_REQ,
  POST_LOGIN_RES,
  POST_REGISTER_ERR,
  POST_REGISTER_REQ,
  POST_REGISTER_RES,
  CLEAR_HELLO_SESSION,
} from '@/shared/actionTypes';
import {
  getAuthorityDetails,
  getModules,
  getPermissions,
  getPlacemarks,
} from '@/shared/actions';
import { translate } from '@/services/TranslationService';
import WEBAPI from '@/services/WEBAPI';
import { setFilters } from '../myCity/actions';
import { getProfile } from '../profile/actions';
import { postAccount } from '../register/actions';

export const helloRequest = (email) => ({
  type: HELLO_REQ,
  payload: { email },
});

export const helloResponse = (code, expiresAt, duration) => ({
  type: HELLO_RES,
  payload: { code, expiresAt, duration },
});

export const helloError = (message) => ({
  type: HELLO_ERR,
  payload: { message },
});

export const clearHelloSession = () => ({
  type: CLEAR_HELLO_SESSION,
});

export const hello = (email, recaptchaToken) => {
  return async (dispatch) => {
    try {
      dispatch(helloRequest(email));

      const response = await WEBAPI.postHelloV2(email, recaptchaToken);

      const { result, error } = response;

      if (error) throw new Error(error);

      const { code, duration } = result;
      const expiresAt = duration ? Date.now() + duration * 1000 : undefined;

      dispatch(helloResponse(code, expiresAt, duration));
      return code;
    } catch (error) {
      console.error('Error in hello action:', error);
      dispatch(helloError('An error occurred while processing your request.'));
      throw error;
    }
  };
};

export const postLoginRequest = () => ({
  type: POST_LOGIN_REQ,
});

export const postLoginResponse = (auth_token, user_sso_id) => ({
  type: POST_LOGIN_RES,
  payload: { auth_token, user_sso_id },
});

export const postLoginError = (message) => ({
  type: POST_LOGIN_ERR,
  payload: { message },
});

export const storeTokensToLocalStorage = (auth_token, user_sso_id) => {
  if (typeof localStorage === 'undefined') {
    throw new Error('LocalStorage is not available in this environment.');
  }

  localStorage.setItem('auth_token', auth_token);
  localStorage.setItem('user_sso_id', user_sso_id);
  localStorage.setItem('hasRegistered', true);
};

export const afterLoginProcess = () => {
  return async (dispatch, getState) => {
    localStorage.setItem('parkingLoggedInOnce', true);

    try {
      await dispatch(getProfile());
      await dispatch(getAuthorityDetails());
      await dispatch(getPermissions());
      await dispatch(getModules());
      await dispatch(postAccount());

      const isMyCityModuleEnabled = getState()?.otherReducer?.modules?.some(
        (module) => module.name === 'mycity'
      );

      const authority = getState()?.profileReducer?.profile?.authority?.name;

      if (isMyCityModuleEnabled && authority) {
        await dispatch(getPlacemarks());
      }

      dispatch(
        setFilters([
          {
            name: 'REPORTS',
          },
        ])
      );
    } catch (error) {
      console.error('Error in afterLoginProcess:', error);
    }
  };
};

export const postLogin = () => {
  return async (dispatch, getState) => {
    try {
      const { code, email } = getState().authReducer;

      if (!code) {
        throw new Error('Code is not available. Please call hello first.');
      }

      dispatch(postLoginRequest());

      const response = await WEBAPI.postLoginV2(email, code);

      const { result, error } = response;

      if (error) throw new Error(error);

      const { token: auth_token, user_sso_id } = result;

      storeTokensToLocalStorage(auth_token, user_sso_id);
      dispatch(postLoginResponse(auth_token, user_sso_id));

      await dispatch(afterLoginProcess());

      return { auth_token, user_sso_id };
    } catch (error) {
      dispatch(postLoginError(translate(error.code)));
      throw error.code || 'An error occurred during login.';
    }
  };
};

export const registerRequest = (name, surname, email_subscription) => ({
  type: POST_REGISTER_REQ,
  payload: { name, surname, email_subscription },
});

export const registerError = (message) => ({
  type: POST_REGISTER_ERR,
  payload: { message },
});

export const registerResponse = (code, expiresAt, duration) => ({
  type: POST_REGISTER_RES,
  payload: { code, expiresAt, duration },
});

export const register = (name, surname, email_subscription, recaptchaToken) => {
  return async (dispatch, getState) => {
    try {
      dispatch(registerRequest(name, surname, email_subscription));

      const { email } = getState().authReducer;

      const response = await WEBAPI.postRegisterV2(
        email,
        name,
        surname,
        email_subscription,
        recaptchaToken
      );

      const { result, error } = response;

      if (error) throw new Error(error);

      const { code, duration } = result;
      const expiresAt = duration ? Date.now() + duration * 1000 : undefined;

      dispatch(registerResponse(code, expiresAt, duration));
      return code;
    } catch (error) {
      console.error('Error in register action:', error);
      dispatch(registerError(translate(error.code)));
      throw error.code || 'An error occurred during registration.';
    }
  };
};

export const logout = () => {
  return (dispatch) => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_sso_id');
    localStorage.removeItem('hasRegistered');

    dispatch({
      type: LOGOUT,
    });
  };
};
